import { getCustomerByIdQuery } from "@domain/customer/infos"
import { useCustomersQuery } from "@hooks/use_customers"
import useTranslate from "@hooks/use_translate"
import { Autocomplete, Box, TextField } from "@mui/material"
import CustomerAiTestPage from "@pages/customer/infos/tabs/ai_test"
import { useQuery } from "@tanstack/react-query"
import { useEffect, useRef, useState } from "react"
import { useSearchParams } from "react-router-dom"

// Kept separate from the AI Logs page's "ai-hub-customer-id" key so picking a
// customer here doesn't change the customer filter on the AI Logs page.
const AI_HUB_SIMULATION_CUSTOMER_KEY = "ai-hub-simulation-customer-id"

export default function AiSimulationHubPage() {
  const { translate } = useTranslate()
  const { customerOptions, isLoading: isCustomersLoading } =
    useCustomersQuery(true)
  const [searchParams, setSearchParams] = useSearchParams()
  // Consumed once on mount so a manual customer/message change afterwards
  // doesn't keep re-triggering the auto-fetch from a stale URL.
  const consumedUrlParams = useRef(false)
  const initialMessageIdFromUrl = useRef(
    searchParams.get("messageId") || undefined
  ).current

  const [customerId, setCustomerId] = useState<string>(() => {
    const fromUrl = searchParams.get("customerId")
    if (fromUrl) return fromUrl
    try {
      return localStorage.getItem(AI_HUB_SIMULATION_CUSTOMER_KEY) || ""
    } catch {
      return ""
    }
  })

  useEffect(() => {
    if (consumedUrlParams.current) return
    consumedUrlParams.current = true
    if (searchParams.get("customerId") || searchParams.get("messageId")) {
      setSearchParams({}, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    try {
      if (customerId) {
        localStorage.setItem(AI_HUB_SIMULATION_CUSTOMER_KEY, customerId)
      } else {
        localStorage.removeItem(AI_HUB_SIMULATION_CUSTOMER_KEY)
      }
    } catch {
      // ignore
    }
  }, [customerId])

  const { data: selectedCustomer } = useQuery({
    queryKey: ["ai-hub-simulation-customer", customerId],
    queryFn: async () => {
      if (!customerId) {
        return undefined
      }
      const response = await getCustomerByIdQuery(customerId)
      return response.isSuccess ? response.data : undefined
    },
    enabled: !!customerId,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false
  })

  const customerPicker = (
    <Box sx={{ maxWidth: 420 }}>
      <Autocomplete<{ id: string; label: string }>
        size="small"
        options={customerOptions}
        disabled={isCustomersLoading}
        value={
          customerOptions.find((option) => option.id === customerId) ?? null
        }
        onChange={(_, option) => setCustomerId(option?.id ?? "")}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        renderInput={(params) => (
          <TextField
            {...params}
            label={translate("customer")}
            placeholder={translate("ai_simulation_select_customer_placeholder")}
          />
        )}
      />
    </Box>
  )

  return (
    <CustomerAiTestPage
      customer={selectedCustomer}
      translate={translate}
      customerPicker={customerPicker}
      initialMessageId={initialMessageIdFromUrl}
    />
  )
}
